/**
 * @lines98/client/screens/support-screen — экран «Поддержать» (крипто-донат L0).
 *
 * «Веточка» (CLAUDE.md «ствол → ветки»): ТОНКАЯ ветка — монтирует ОБЩИЕ модули и
 * задаёт только раскладку/вид. Своего движка не верстает:
 *   - карты приёма — переиспускаемый примитив `components/crypto-receive-card` (QR +
 *     адрес + копи-морф), по одной на цель из ЕДИНОГО конфига `lib/donate-targets`
 *     (порядок = USDT-первой, owner 2026-06-26);
 *   - тост-фидбэк копирования — общий `ui/toast` (не свой механизм);
 *   - все тексты — каталог i18n через `t()` (ноль литералов; новый язык подхватывается
 *     без правки экрана). Бренд/тикеры (USDT/TRON/ETH/BTC) — в конфиге, они не переводятся.
 *
 * ПОЗИЦИОНИРОВАНИЕ (owner 2026-06-26): тёплая благодарность, НЕ касса. Прямой честный
 * текст «донат ничего не даёт в игре, это спасибо, а не покупка» — доверие = премиальность.
 * Идентичность «без pay-to-win»: сервер о платеже не знает (L0, ADR-0024 §8).
 *
 * СКРОЛЛ (§1.2 content-exception, как лендинг/content-screen): страница КОНТЕНТ-богатая
 * (герой + 3 карты с QR + тексты) — на узком вьюпорте легитимно скроллится ЯВНО
 * (overflow-y:auto), на широком помещается в экран без скролла. P1 (адрес+копирование
 * каждой карты) доступен всегда. Это НЕ игровой viewport-locked экран.
 *
 * Layout/visual — styles/support.css (layout-plan §5.0 там).
 * Cross-references: ADR-0024 addendum §7/§8; responsive-ui §0.A/§0.B/§5.0; lib/donate-targets.
 */

import '../styles/support.css';

import {
  mountCryptoReceiveCard,
  type CryptoReceiveCardHandle,
} from '../components/crypto-receive-card.js';
import { attachAutofit, type AutofitHandle } from '../lib/autofit-text.js';
import { DONATE_TARGETS } from '../lib/donate-targets.js';
import { t } from '../lib/i18n/index.js';
import { attachTitleFit } from '../lib/title-fit.js';
import { FS_BUTTON_MIN_PX } from '../lib/viewport-sizer.js';
import { hideToast, showToast } from '../ui/toast.js';
import type { ScreenHandle } from './board-screen.js';

/** Маленькие inline-глифы бенефит-чипов (Tabler outline). */
const ICON_INFINITY =
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9.828 9.172a4 4 0 1 0 0 5.656a10 10 0 0 0 2.172 -2.828a10 10 0 0 1 2.172 -2.828a4 4 0 1 1 0 5.656a10 10 0 0 1 -2.172 -2.828a10 10 0 0 0 -2.172 -2.828"/></svg>`;
const ICON_SPARK =
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M16 18a2 2 0 0 1 2 2a2 2 0 0 1 2 -2a2 2 0 0 1 -2 -2a2 2 0 0 1 -2 2zm0 -12a2 2 0 0 1 2 2a2 2 0 0 1 2 -2a2 2 0 0 1 -2 -2a2 2 0 0 1 -2 2zm-7 12a6 6 0 0 1 6 -6a6 6 0 0 1 -6 -6a6 6 0 0 1 -6 6a6 6 0 0 1 6 6z"/></svg>`;
const ICON_SHIELD =
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3a12 12 0 0 0 8.5 3a12 12 0 0 1 -8.5 15a12 12 0 0 1 -8.5 -15a12 12 0 0 0 8.5 -3"/><path d="M9 12l2 2l4 -4"/></svg>`;

/** 3 бенефит-чипа «на что идёт поддержка» (icon + ключ каталога). */
const BENEFITS: readonly { readonly key: string; readonly icon: string }[] = [
  { key: 'replays', icon: ICON_INFINITY },
  { key: 'modes', icon: ICON_SPARK },
  { key: 'noP2w', icon: ICON_SHIELD },
];

export function mountSupportScreen(host: HTMLElement): ScreenHandle {
  host.innerHTML = '';

  const root = document.createElement('div');
  root.className = 'support-screen screen-root';

  // === Заголовок «♥ Поддержать» (♥ — декоративный, текст из каталога) ===========
  const header = document.createElement('header');
  header.className = 'support-header';
  header.innerHTML =
    `<span class="support-heart" aria-hidden="true">` +
    `<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M6.979 3.074a6 6 0 0 1 4.988 1.425l.037 .033l.034 -.03a6 6 0 0 1 4.733 -1.44l.246 .036a6 6 0 0 1 3.364 10.008l-.18 .185l-.048 .041l-7.45 7.379a1 1 0 0 1 -1.313 .082l-.094 -.082l-7.493 -7.422a6 6 0 0 1 3.476 -10.46z"/></svg>` +
    `</span>` +
    `<h1 class="support-title screen-title">${esc(t('game.support.title'))}</h1>`;

  // === Герой: линия из цветных шаров (на-бренд «собранная линия» = радость игры) ==
  const hero = document.createElement('div');
  hero.className = 'support-hero';
  hero.setAttribute('aria-hidden', 'true');
  for (let i = 1; i <= 5; i++) {
    const ball = document.createElement('span');
    ball.className = 'support-ball';
    ball.style.setProperty('--i', String(i));
    hero.appendChild(ball);
  }

  // === Герой-абзац ===============================================================
  const heroText = document.createElement('p');
  heroText.className = 'support-hero-text';
  heroText.textContent = t('game.support.hero');

  // === Бенефит-чипы (на что идёт поддержка) ======================================
  const benefits = document.createElement('div');
  benefits.className = 'support-benefits';
  benefits.setAttribute('role', 'list');
  for (const b of BENEFITS) {
    const chip = document.createElement('div');
    chip.className = 'support-benefit';
    chip.setAttribute('role', 'listitem');
    chip.innerHTML =
      `<span class="support-benefit-icon" aria-hidden="true">${b.icon}</span>` +
      `<span class="support-benefit-label">${esc(t(`game.support.kpi.${b.key}`))}</span>`;
    benefits.appendChild(chip);
  }

  // === Карты приёма (из конфига; USDT-первой) ====================================
  // Полоса карт (.support-cards — капнута + центрирована) > грид (.support-cards-grid —
  // auto-fit 1/2/3 от cell). Карты монтируются в ГРИД.
  const cardsEl = document.createElement('div');
  cardsEl.className = 'support-cards';
  const cardsGrid = document.createElement('div');
  cardsGrid.className = 'support-cards-grid';
  cardsEl.appendChild(cardsGrid);
  const cards: CryptoReceiveCardHandle[] = [];
  for (const target of DONATE_TARGETS) {
    const card = mountCryptoReceiveCard(cardsGrid, {
      target,
      networkLabel: t('game.support.network'),
      copyLabel: t('game.support.copy'),
      copiedLabel: t('game.support.copied'),
      // Успех горит дольше (owner 2026-06-27 «хотя бы 5 сек») — игрок успевает прочесть
      // «Адрес скопирован…»; ошибка — короче. showToast принимает длительность (мс).
      onCopy: (ok) =>
        showToast(
          ok ? t('game.support.toastOk') : t('game.support.toastFail'),
          ok ? 5000 : 3000,
        ),
    });
    cards.push(card);
  }

  // === Микротекст «проверьте сеть» (честная строка) =============================
  const note = document.createElement('p');
  note.className = 'support-note';
  note.textContent = t('game.support.networkNote');

  // === Подвал-нота благодарности ================================================
  const thanks = document.createElement('p');
  thanks.className = 'support-thanks';
  thanks.textContent = t('game.support.footerNote');

  root.append(header, hero, heroText, benefits, cardsEl, note, thanks);

  // REVEAL-GATE (как у стартовой): прячем до устаканивания раскладки, затем показываем.
  root.dataset.mounting = '';
  host.appendChild(root);
  requestAnimationFrame(() =>
    requestAnimationFrame(() => root.removeAttribute('data-mounting')),
  );

  // AUTOFIT (измерение → текст НЕ вылезает, единый закон со всеми экранами):
  //   · заголовок — общий attachTitleFit (.screen-title, как board/daily/profile/replay);
  //   · подписи кнопок копирования — group-uniform line-fit от ШИРИНЫ кнопки (heightEl =
  //     сама пилюля с min-height --btn-h → идемпотентно на resize), capToDesign + floor.
  //     Текст кнопки берёт размер ОТ КНОПКИ → коллизия невозможна по построению.
  const titleFit = attachTitleFit(root);
  const cardTextFit: AutofitHandle = attachAutofit(root, [
    {
      selector: '.crypto-copy .pill-button-label',
      uniform: true,
      mode: 'line',
      heightEl: cardsGrid.querySelector<HTMLElement>('.crypto-copy') ?? undefined,
      capToDesign: true,
      minPx: FS_BUTTON_MIN_PX,
    },
    {
      // Адрес — ОДНА СТРОКА БЕЗ ПЕРЕНОСА (owner-rule 2026-06-27: переносы в адресе кошелька
      // запрещены) → шрифт ужимается, пока ВЕСЬ адрес влезает в ширину карты. mode:'word'
      // (адрес = одно «слово»). uniform — 3 адреса одного размера (по длиннейшему). Floor 8px.
      selector: '.crypto-addr',
      uniform: true,
      mode: 'word',
      capToDesign: true,
      minPx: 8,
    },
  ]);

  return {
    unmount(): void {
      // Гасим тост при уходе: 5с «Адрес скопирован…» не должен висеть на след. экране
      // (owner-directive 2026-06-27).
      hideToast();
      titleFit.detach();
      cardTextFit.detach();
      for (const c of cards) c.destroy();
      host.innerHTML = '';
    },
  };
}

/** HTML-escape для текстовых позиций. */
function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
